import { COLORS, PAGINATION_CONFIGS } from 'config/constants';
import React from 'react';
import ReactSelect, { StylesConfig } from 'react-select';
import styled from 'styled-components';

import Flex from 'components/Flex';
import Select, { customSelectStyles } from 'components/Form/Inputs/Select/Select';
import { ChevronIcon } from 'components/Icons';

type PageOption = {
  label: string;
  value: number;
};

const customStyles: StylesConfig<PageOption, false> = {
  option: customSelectStyles<PageOption>().option,
  control: provided => ({
    ...provided,
    border: 'none',
    minHeight: 'none',
    boxShadow: 'none',
    minWidth: '10.8rem'
  }),
  valueContainer: provided => ({
    ...provided,
    paddingRight: 0,
    fontWeight: 'bold',
    display: 'flex'
  }),
  singleValue: provided => ({
    ...provided,
    color: '#7d7d7d'
  }),
  menuPortal: provided => ({
    ...provided,
    width: '8rem',
    zIndex: 5 // above footer
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '4px'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  })
};

const TablePaginationControlsButton = styled.button<{
  $left?: boolean;
  $right?: boolean;
}>`
  color: ${COLORS.PRIMARY};
  display: flex;
  font-size: 1.4rem;
  background: none;
  svg {
    ${({ $left, $right }) => {
      if ($left) return `margin-right: 0.3rem;`;
      if ($right) return `margin-left: 0.3rem;`;
    }}
  }
`;

const TablePaginationControlsPageSelect = styled.div<{ $enabled?: boolean }>`
  align-items: center;
  display: flex;
  margin: 0 0.8rem;

  ${({ $enabled }) =>
    $enabled &&
    `
    font-weight: bold;
  `}

  svg {
    margin-left: 0.3rem;
  }
`;

const FlexContainer = styled(Flex)`
  margin-top: 10px;
`;

/**
 * [TablePaginationControls] - component that handles controlling table pagination. Goes at the footer of the table
 */

interface Props {
  currentPage: number;
  nodeCount: number;
  setPageCount: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  // Optional
  className?: string;
  style?: React.CSSProperties;
  isFetching?: boolean;
  error?: boolean;
  itemsPerPage?: number;
  itemsPerPageOptions?: number[];
}

const TablePaginationControls = ({
  currentPage,
  nodeCount,
  setPageCount,
  setItemsPerPage,
  // Optional
  className,
  style,
  isFetching,
  error,
  itemsPerPage = PAGINATION_CONFIGS.DEFAULT_ITEMS_PER_PAGE,
  itemsPerPageOptions = PAGINATION_CONFIGS.ITEMS_PER_PAGE_OPTIONS
}: Props) => {
  const totalPages = Math.ceil(nodeCount / itemsPerPage);
  const hasNextPage = currentPage * itemsPerPage < nodeCount;
  const hasPreviousPage = currentPage > 1;

  if ((isFetching && isNaN(totalPages)) || (!isFetching && !totalPages) || error) {
    return null;
  }

  // When items per page changes, we want to maintain the same relative page position
  // for the last result they saw (bottom of table). When adjusting itemsPerPage, we adjust
  // the pageCount to the page where that result will be located
  const updateItemsPerPage = (nextItemsPerPage: number | undefined) => {
    if (!nextItemsPerPage) return;
    const currentPostion = Math.min(itemsPerPage * currentPage, nodeCount);
    const nextTotalPages = Math.ceil(nodeCount / nextItemsPerPage);

    // If the current page is 1, keep at page 1, else the experience is weird
    const nextPageCount =
      currentPage === 1
        ? 1
        : Math.min(Math.ceil(currentPostion / nextItemsPerPage), nextTotalPages);

    setItemsPerPage(nextItemsPerPage);
    setPageCount(nextPageCount);
  };

  const pageOptions: PageOption[] = new Array(totalPages).fill(0).map((_, i) => ({
    value: i + 1,
    label: (i + 1).toString()
  }));

  return (
    <FlexContainer
      justify="space-between"
      align="flex-start"
      className={className}
      style={style}
    >
      <Flex align="center" grow={0}>
        <TablePaginationControlsButton
          onClick={() => setPageCount(currentPage - 1)}
          disabled={isFetching || !hasPreviousPage}
          $left
        >
          <ChevronIcon
            direction={ChevronIcon.DIRECTIONS.LEFT}
            color="currentColor"
            size={14}
          />
          Previous
        </TablePaginationControlsButton>

        {totalPages === 1 ? (
          <TablePaginationControlsPageSelect>
            Page {currentPage} of {totalPages}
          </TablePaginationControlsPageSelect>
        ) : (
          <ReactSelect<PageOption>
            inputId="page-number"
            name="page-number"
            value={{ label: `Page ${currentPage} of ${totalPages}`, value: currentPage }}
            options={pageOptions}
            menuPlacement="auto"
            maxMenuHeight={180}
            onChange={option => option && setPageCount(option.value)}
            styles={customStyles}
            menuPortalTarget={document.getElementById('popovers-root')}
          />
        )}

        <TablePaginationControlsButton
          onClick={() => setPageCount(currentPage + 1)}
          disabled={isFetching || !hasNextPage}
          $right
        >
          Next
          <ChevronIcon
            direction={ChevronIcon.DIRECTIONS.RIGHT}
            color="currentColor"
            size={14}
          />
        </TablePaginationControlsButton>
      </Flex>
      <div />
      {itemsPerPage && (
        <Flex align="center" grow={0}>
          <div className="mr-1">Items per page:</div>
          <Select
            customTheme="button"
            menuPortalTarget={document.getElementById('popovers-root')}
            onChange={option => updateItemsPerPage(option?.value)}
            options={itemsPerPageOptions.map(value => ({
              label: value.toString(),
              value
            }))}
            value={{ label: itemsPerPage.toString(), value: itemsPerPage }}
            style={{ minWidth: '6.8rem' }}
          />
        </Flex>
      )}
    </FlexContainer>
  );
};

export default TablePaginationControls;
